<script setup>
/* eslint semi: 1 */

// Package imports
import { computed, ref, inject, onMounted, useTemplateRef } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { signIn as AmplifySignIn, confirmSignIn as AmplifyConfirmSignIn } from 'aws-amplify/auth';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';

// Component imports
import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';

import { t } from '@/i18n/i18ns';
import CONSTANTS from '@/globals/constants';
import EventBus from '@/components/mixins/event-bus-mixin';

const $router = inject('$routerInstance');

const props = defineProps({
  email: {
    type: String,
    required: true,
  },
  stage: {
    type: String,
    required: true,
  },
  isLoginInvite: {
    type: Boolean,
    default: false,
  },
  isSignInMini: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['successSubmit', 'editEmail', 'userNotConfirmed', 'resetPassword']);
const passwordInput = useTemplateRef('password-input');

const schema = yup.object({
  password: yup.string()
    .required('label.requiredPassword'),
});

const { errors, handleSubmit, defineField, isSubmitting, resetForm } = useForm({
  validationSchema: schema,
});

const [password] = defineField('password');
const cognitoError = ref(null);
const cognitoRequestProcessing = ref(false);
const isNewPasswordRequired = ref(false);
const cognitoUser = ref(null);

const cognitoAuthStore = useCognitoAuthStore();

const passwordInputPlaceholder = computed(() => {
  if (isNewPasswordRequired.value) {
    return t('label.newPassword');
  } else if (props.isLoginInvite) {
    return t('label.temporaryPassword');
  } else {
    return t('label.password');
  }
});

function onChangeEmail () {
  if (isSubmitting.value || cognitoRequestProcessing.value) {
    return;
  }

  emit('editEmail');
}

function onResetPassword () {
  if (isSubmitting.value || cognitoRequestProcessing.value) {
    return;
  }

  emit('resetPassword');
}

const handleSuccessfulAuthentication = async () => {
  const session = await cognitoAuthStore.fetchAndStoreAuthSession();
  cognitoRequestProcessing.value = false;

  console.log('Authentication successful: ', session);
  emit('successSubmit', { success: true, cognitoUser: cognitoUser.value });
};

const handleFailedAuthentication = (error) => {
  // Handle authentication failure
  console.log('Authentication failed:', error, error.message);
  if (error.name === CONSTANTS.USER_LAMBDA_VALIDATION_EXCEPTION) {
    if (error.message.includes('Not allowed to authenticate via non-SSO Provider')) {
      cognitoError.value = t('errors.nonSSOAuthDenied');
    } else {
      cognitoError.value = error.message;
    }
  } else if (error.name === CONSTANTS.USER_NOT_FOUND) {
    cognitoError.value = t('label.userNotFound');
  } else if (error.name === CONSTANTS.NOT_AUTHORIZED) {
    const hasTemporaryPasswordExpired = error.message.includes('Temporary password has expired');
    if (hasTemporaryPasswordExpired) {
      EventBus.$emit(
        'SHOW_CUSTOM_NOTIFICATION',
        {
          type: 'error',
          alert: {
            message: t('label.tempPasswordExpiredPleaseContactAdmin')
          },
          callbacks: { eventOnClose: () => { $router.push('/', () => {}); } }
        }
      );
    } else {
      cognitoError.value = t('label.credentialsNotValid');
    }
  } else if (error.name === CONSTANTS.INVALID_PASSWORD) {
    EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
      type: 'error',
      alert: {
        message: t('label.passwordPolicyRequirementText')
      },
    });
  } else {
    cognitoError.value = t('label.loginError');
  }
};

const handleNewPasswordRequired = () => {
  console.log('New password required');
  isNewPasswordRequired.value = true;
  emit('successSubmit', { isNewPasswordRequired: isNewPasswordRequired.value });
  resetForm({ values: { password: '' } }); // Reset form so we can reuse password for setting new password
};

async function authenticateUser () {
  cognitoRequestProcessing.value = true;

  try {
    await cognitoAuthStore.configureWithEmail(props.email);

    let isSignedIn, nextStep;
    if (isNewPasswordRequired.value) {
      const response = await AmplifyConfirmSignIn({
        challengeResponse: password.value
      });
      isSignedIn = response.isSignedIn;
      nextStep = response.nextStep;
    } else {
      const response = await AmplifySignIn({
        username: props.email,
        password: password.value
      });
      isSignedIn = response.isSignedIn;
      nextStep = response.nextStep;
    }

    if (isSignedIn && nextStep.signInStep === 'DONE') {
      await handleSuccessfulAuthentication();
    } else if (nextStep && nextStep.signInStep === 'CONFIRM_SIGN_UP') {
      emit('userNotConfirmed', { email: props.email, password: password.value });
    } else if (nextStep && nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      handleNewPasswordRequired();
    }
  } catch (error) {
    handleFailedAuthentication(error);
  } finally {
    cognitoRequestProcessing.value = false;
  }
}

const onSubmit = handleSubmit(async () => {
  // Reset errors for each submit cycle
  cognitoError.value = null;
  await authenticateUser();
});

onMounted(() => {
  if (passwordInput.value) {
    passwordInput.value.focus();
  }
});

</script>

<template>
  <form
    class="max-[1919px]:my-3 min-[1920px]:my-5 flex flex-col flex-1 w-full justify-between"
    data-component-element-id="sign-in-password-form"
    @submit="onSubmit"
  >
    <div
      v-if="!isNewPasswordRequired"
      class="max-[1919px]:text-xs min-[1920px]:text-base text-white pt-3 text-left"
    >
      <span>{{ $t('texts.youAreLoggingIn') }}</span>
      <div class="flex items-center justify-between">
        <span class="max-[1919px]:max-w-44 min-[1920px]:max-w-72 font-bold break-words mr-1" data-component-element-id="sign-in-email-value">{{ email }}</span>
        <img
          class="w-3 h-3"
          :class="{
            'cursor-pointer': !isSubmitting && !cognitoRequestProcessing,
            'cursor-not-allowed': isSubmitting || cognitoRequestProcessing,
            'opacity-40': isSubmitting || cognitoRequestProcessing
          }"
          data-component-element-id="sign-in-email-edit-icon"
          src="@/static/icons/pencil_v2_white.svg"
          alt="Edit Icon"
          @click="onChangeEmail"
        />
      </div>
    </div>

    <div v-if="isNewPasswordRequired" />
    <!-- Form -->
    <div class="flex flex-col">
      <form-input
        id="password"
        ref="password-input"
        v-model="password"
        type="password"
        autocomplete="password"
        :label="passwordInputPlaceholder"
        inputTheme="light"
        :size="isSignInMini ? 'xs' : 'md'"
        required
        :disabled="isSubmitting || cognitoRequestProcessing"
        :validationStatus="errors.password ? 'error' : undefined"
        data-component-element-id="sign-in-password-input"
      >
        <template
          v-if="errors?.password"
          #validationTransparent
        >
          {{ $t(errors.password) }}
        </template>
      </form-input>

      <div v-if="cognitoError">
        <p
          class="text-white uppercase"
          :class="{
            'text-xs mt-4': isSignInMini,
            'text-base mt-6': !isSignInMini,
          }"
        >
          {{ cognitoError }}
        </p>
      </div>
    </div>

    <div class="w-full justify-center flex flex-col items-center">
      <form-button
        type="submit"
        buttonTheme="light"
        :size="isSignInMini ? 'xs' : 'md'"
        data-component-element-id="sign-in-password-submit-btn"
        :loading="isSubmitting || cognitoRequestProcessing"
        :disabled="isSubmitting || cognitoRequestProcessing"
        :label="isNewPasswordRequired ? $t('label.confirm') : $t('label.login')"
        @click="onSubmit"
      />

      <div
        v-if="!isNewPasswordRequired"
        class="max-[1919px]:text-[10px]/[10px] min-[1920px]:text-xs max-[1919px]:pt-3 min-[1920px]:pt-5 uppercase"
        :class="{
          'cursor-pointer text-white': !isSubmitting && !cognitoRequestProcessing,
          'cursor-not-allowed text-gray-400': isSubmitting || cognitoRequestProcessing
        }"
        data-component-element-id="sign-in-password-forgot-password"
        @click="onResetPassword"
      >
        <span>{{ $t('label.forgotPassword') }}?</span>
      </div>
    </div>
  </form>
</template>
